import { useState, type FormEvent } from 'react';
import { site } from '@/mocks/site';

const FORM_URL = 'https://readdy.ai/api/form/dahfqaboh653ivfvntlg';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [messageLength, setMessageLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('contact_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setMessageLength(0);
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'contact_alt') return;
      const text = String(value).trim();
      if (text) payload.append(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessageLength(0);
        form.reset();
      } else {
        setStatus('error');
        setFormError(
          typeof serverMsg === 'string' && serverMsg.trim()
            ? serverMsg
            : '提交未成功，请稍后重试，或直接发送邮件联系我们。',
        );
      }
    } catch {
      setStatus('error');
      setFormError(`网络异常，未能提交。请稍后重试，或直接发送邮件至 ${site.email}。`);
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-8 text-center md:p-10">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
          <i className="ri-check-line text-2xl" />
        </span>
        <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">已经收到你的信息</h2>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-foreground-700">
          感谢你的信任。我们会在{site.replyTime}内查看，并在需要补充细节时与你联系。涉及个人身份的信息会全程脱敏处理。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 px-5 py-2.5 font-label text-sm text-secondary-900 transition-colors hover:border-secondary-500"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="zhijing-contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-2 block font-label text-sm text-foreground-800">
            称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            autoComplete="name"
            placeholder="怎么称呼你都可以，可用化名"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-2 block font-label text-sm text-foreground-800">
            邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="用于接收回复，不会公开"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="contact-company" className="mb-2 block font-label text-sm text-foreground-800">
            涉及公司或平台
          </label>
          <input
            id="contact-company"
            name="company"
            type="text"
            placeholder="选填，便于我们核对公开资料"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div>
          <label htmlFor="contact-topic" className="mb-2 block font-label text-sm text-foreground-800">
            事项类型
          </label>
          <select
            id="contact-topic"
            name="topic"
            defaultValue="爆料投稿"
            className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          >
            <option value="爆料投稿">爆料投稿</option>
            <option value="更正与申明">更正与申明</option>
            <option value="内容建议">内容建议</option>
            <option value="其他咨询">其他咨询</option>
          </select>
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-2 flex items-center justify-between">
          <label htmlFor="contact-message" className="font-label text-sm text-foreground-800">
            想告诉我们的事 <span className="text-primary-600">*</span>
          </label>
          <span className="font-label text-xs text-foreground-500">{messageLength}/500</span>
        </div>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={6}
          onChange={(event) => setMessageLength(event.target.value.length)}
          placeholder="尽量描述具体的时间、经过与你可提供佐证的资料类型。不必一次性写完全部细节，我们可以后续沟通。"
          className="w-full resize-y rounded-md border border-background-300 bg-background-50 px-3.5 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
      </div>

      <div className="form-field-note" aria-hidden="true">
        <label htmlFor="contact-alt-field">请勿填写此栏</label>
        <input
          id="contact-alt-field"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === 'error' && formError && (
        <div className="mt-5 flex items-start gap-3 rounded-md border border-primary-200 bg-primary-50 px-4 py-3">
          <i className="ri-error-warning-line mt-0.5 text-base text-primary-700" />
          <p className="text-sm leading-relaxed text-primary-800">{formError}</p>
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-label text-xs leading-relaxed text-foreground-500">
          提交即表示你同意我们在脱敏后整理使用相关内容。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '正在提交…' : '提交信息'}
        </button>
      </div>
    </form>
  );
}